<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      due_date: null,
      due_time: null,
      recurrence_type: null,
      recurrence_interval: null,
      recurrence_unit: null,
      recurrence_ends_at: null,
      recurrence_count: null,
    }),
  },
  hideRecurrence: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(today.getDate() + 1);
const viewYear = ref(today.getFullYear());
const viewMonth = ref(today.getMonth());

const selectedDate = ref(props.modelValue?.due_date ?? null);

// horário: dígitos brutos digitados pelo usuário (1-4 chars)
const showTimeInput = ref(!!props.modelValue?.due_time);
const timeRaw = ref('');
const timeInputEl = ref(null);

function parseTime(raw) {
  if (!raw || raw.length === 0) return null;
  const digits = raw.padEnd(4, '0');
  const h = parseInt(digits.slice(0, 2), 10);
  const m = parseInt(digits.slice(2, 4), 10);
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatTimeDisplay(raw) {
  if (!raw) return '';
  if (raw.length <= 2) return raw;
  return `${raw.slice(0, 2)}:${raw.slice(2)}`;
}

function syncInputDisplay() {
  if (!timeInputEl.value) return;
  timeInputEl.value.value = formatTimeDisplay(timeRaw.value);
  const len = timeInputEl.value.value.length;
  timeInputEl.value.setSelectionRange(len, len);
}

watch(showTimeInput, val => {
  if (val)
    nextTick(() => {
      if (!timeInputEl.value) return;
      syncInputDisplay();
    });
});

function initTimeRaw(dueTime) {
  if (!dueTime) return '';
  return dueTime.replace(':', '');
}
timeRaw.value = initTimeRaw(props.modelValue?.due_time ?? '');

const showRecurrenceMenu = ref(false);
const showCustomModal = ref(false);
const recurrenceType = ref(props.modelValue?.recurrence_type ?? null);
const customInterval = ref(props.modelValue?.recurrence_interval ?? 1);
const customUnit = ref(props.modelValue?.recurrence_unit ?? 'days');
const endsMode = ref('never');
const endsAt = ref(props.modelValue?.recurrence_ends_at ?? null);
const endsCount = ref(props.modelValue?.recurrence_count ?? 2);

watch(
  () => props.modelValue,
  val => {
    if (val) {
      selectedDate.value = val.due_date ?? null;
      // Nunca reseta o timeRaw enquanto o picker está aberto — dia e hora são independentes
      recurrenceType.value = val.recurrence_type ?? null;
      customInterval.value = val.recurrence_interval ?? 1;
      customUnit.value = val.recurrence_unit ?? 'days';
      endsAt.value = val.recurrence_ends_at ?? null;
      endsCount.value = val.recurrence_count ?? 2;
    }
  },
  { deep: true }
);

const builtTime = computed(() => parseTime(timeRaw.value));

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const monthLabel = computed(() =>
  new Date(viewYear.value, viewMonth.value, 1)
    .toLocaleDateString('pt-BR', { month: 'long' })
    .replace(/^\w/, c => c.toUpperCase())
);

const calendarDays = computed(() => {
  const firstDay = new Date(viewYear.value, viewMonth.value, 1);
  const lastDay = new Date(viewYear.value, viewMonth.value + 1, 0);
  const days = [];

  for (let i = 0; i < firstDay.getDay(); i += 1) {
    const d = new Date(
      viewYear.value,
      viewMonth.value,
      -firstDay.getDay() + 1 + i
    );
    days.push({ date: d, currentMonth: false });
  }
  for (let i = 1; i <= lastDay.getDate(); i += 1) {
    days.push({
      date: new Date(viewYear.value, viewMonth.value, i),
      currentMonth: true,
    });
  }
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i += 1) {
    days.push({
      date: new Date(viewYear.value, viewMonth.value + 1, i),
      currentMonth: false,
    });
  }
  return days;
});

function isToday(date) {
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function isSelected(date) {
  if (!selectedDate.value) return false;
  const [y, m, d] = selectedDate.value.split('-').map(Number);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

function toLocalISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function emitValue({ close = false, discard = false } = {}) {
  emit('update:modelValue', {
    due_date: selectedDate.value,
    due_time: builtTime.value,
    recurrence_type: recurrenceType.value,
    recurrence_interval:
      recurrenceType.value === 'custom' ? customInterval.value : null,
    recurrence_unit:
      recurrenceType.value === 'custom' ? customUnit.value : null,
    recurrence_ends_at: endsMode.value === 'on_date' ? endsAt.value : null,
    recurrence_count: endsMode.value === 'after' ? endsCount.value : null,
    _close: close,
    _discard: discard,
  });
}

function commitTime() {
  if (timeRaw.value.length >= 1 && parseTime(timeRaw.value)) emitValue();
}

function clearTime() {
  timeRaw.value = '';
  showTimeInput.value = false;
  emit('update:modelValue', {
    due_date: selectedDate.value,
    due_time: null,
    recurrence_type: recurrenceType.value,
    recurrence_interval:
      recurrenceType.value === 'custom' ? customInterval.value : null,
    recurrence_unit:
      recurrenceType.value === 'custom' ? customUnit.value : null,
    recurrence_ends_at: endsMode.value === 'on_date' ? endsAt.value : null,
    recurrence_count: endsMode.value === 'after' ? endsCount.value : null,
    _close: false,
  });
}

function onTimeKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitTime();
    showTimeInput.value = false;
    return;
  }
  if (e.key === 'Escape') {
    e.preventDefault();
    timeRaw.value = '';
    syncInputDisplay();
    showTimeInput.value = false;
    emitValue();
    return;
  }

  if (e.key === 'Backspace') {
    e.preventDefault();
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    // Se tem seleção, apaga a seleção; senão apaga o dígito antes do cursor
    const display = formatTimeDisplay(timeRaw.value);
    const selEnd = el.selectionEnd;
    if (pos !== selEnd) {
      // tem seleção — remove os dígitos selecionados
      const digits = timeRaw.value.split('');
      // mapeia posições do display para índices dos dígitos
      let newRaw = '';
      let di = 0;
      for (let i = 0; i < display.length; i += 1) {
        if (display[i] !== ':') {
          if (i < pos || i >= selEnd) newRaw += digits[di];
          di += 1;
        }
      }
      timeRaw.value = newRaw;
    } else if (pos > 0) {
      // sem seleção — apaga dígito antes do cursor
      // pos no display; pula ':' para achar índice no raw
      let rawIdx = 0;
      for (let i = 0; i < pos; i += 1) {
        if (display[i] !== ':') rawIdx += 1;
      }
      rawIdx -= 1; // dígito antes do cursor
      if (rawIdx >= 0) {
        timeRaw.value =
          timeRaw.value.slice(0, rawIdx) + timeRaw.value.slice(rawIdx + 1);
      }
    }
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    // reposiciona cursor
    let newPos = pos > 0 ? pos - 1 : 0;
    if (newPos > 0 && newDisplay[newPos - 1] === ':') newPos -= 1;
    el.setSelectionRange(newPos, newPos);
    if (parseTime(timeRaw.value)) emitValue();
    return;
  }

  if (/^\d$/.test(e.key)) {
    e.preventDefault();
    if (timeRaw.value.length >= 4) return;
    const el = timeInputEl.value;
    const pos = el.selectionStart;
    const display = formatTimeDisplay(timeRaw.value);
    // mapeia posição do display para índice no raw
    let rawIdx = 0;
    for (let i = 0; i < pos; i += 1) {
      if (display[i] !== ':') rawIdx += 1;
    }
    timeRaw.value =
      timeRaw.value.slice(0, rawIdx) + e.key + timeRaw.value.slice(rawIdx);
    const newDisplay = formatTimeDisplay(timeRaw.value);
    el.value = newDisplay;
    // avança cursor (pula ':' se necessário)
    let newPos = pos + 1;
    if (newDisplay[newPos] === ':') newPos += 1;
    el.setSelectionRange(newPos, newPos);
    if (parseTime(timeRaw.value)) emitValue();
  }
}

function selectDay(date) {
  selectedDate.value = toLocalISODate(date);
  emitValue();
}

function prevMonth() {
  if (viewMonth.value === 0) {
    viewMonth.value = 11;
    viewYear.value -= 1;
  } else {
    viewMonth.value -= 1;
  }
}

function nextMonth() {
  if (viewMonth.value === 11) {
    viewMonth.value = 0;
    viewYear.value += 1;
  } else {
    viewMonth.value += 1;
  }
}

function prevYear() {
  viewYear.value -= 1;
}

function nextYear() {
  viewYear.value += 1;
}

function setToday() {
  selectedDate.value = toLocalISODate(today);
  viewYear.value = today.getFullYear();
  viewMonth.value = today.getMonth();
  emitValue();
}

function setTomorrow() {
  selectedDate.value = toLocalISODate(tomorrow);
  viewYear.value = tomorrow.getFullYear();
  viewMonth.value = tomorrow.getMonth();
  emitValue();
}

function clearDate() {
  selectedDate.value = null;
  recurrenceType.value = null;
  timeRaw.value = '';
  showTimeInput.value = false;
  emitValue({ close: true });
}

const RECURRENCE_OPTIONS = [
  { key: 'daily', label: () => t('TASKS.RECURRENCE.DAILY') },
  { key: 'weekdays', label: () => t('TASKS.RECURRENCE.WEEKDAYS') },
  { key: 'weekly', label: () => t('TASKS.RECURRENCE.WEEKLY') },
  { key: 'monthly', label: () => t('TASKS.RECURRENCE.MONTHLY') },
  { key: 'custom', label: () => t('TASKS.RECURRENCE.CUSTOM') },
  { key: 'flexible', label: () => t('TASKS.RECURRENCE.FLEXIBLE') },
];

const UNIT_OPTIONS = [
  { key: 'days', label: () => t('TASKS.RECURRENCE.UNITS.DAYS') },
  { key: 'weeks', label: () => t('TASKS.RECURRENCE.UNITS.WEEKS') },
  { key: 'months', label: () => t('TASKS.RECURRENCE.UNITS.MONTHS') },
  { key: 'years', label: () => t('TASKS.RECURRENCE.UNITS.YEARS') },
];

function selectRecurrence(key) {
  if (key === 'custom') {
    showRecurrenceMenu.value = false;
    showCustomModal.value = true;
    return;
  }
  recurrenceType.value = recurrenceType.value === key ? null : key;
  showRecurrenceMenu.value = false;
  emitValue({ close: true });
}

function clearRecurrence() {
  recurrenceType.value = null;
  customInterval.value = 1;
  customUnit.value = 'days';
  endsMode.value = 'never';
  endsAt.value = null;
  endsCount.value = 2;
  showRecurrenceMenu.value = false;
  emitValue({ close: true });
}

function cancelCustom() {
  showCustomModal.value = false;
}

watch([customInterval, customUnit, endsMode, endsAt, endsCount], () => {
  if (showCustomModal.value) {
    recurrenceType.value = 'custom';
    emitValue({ close: false });
  }
});

defineExpose({ save: () => emitValue({ close: true }) });
</script>

<template>
  <div class="relative" tabindex="-1" @click.stop>
    <!-- Calendário principal -->
    <div
      class="bg-n-background border border-n-weak rounded-xl shadow-xl w-72 overflow-hidden"
    >
      <!-- Atalhos -->
      <div class="flex gap-1.5 px-3 py-2 border-b border-n-weak">
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors font-medium whitespace-nowrap"
          :class="
            selectedDate === toLocalISODate(today)
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="setToday"
        >
          {{ t('TASKS.DATE_SHORTCUTS.TODAY') }}
        </button>
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
          :class="
            selectedDate === toLocalISODate(tomorrow)
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="setTomorrow"
        >
          {{ t('TASKS.DATE_SHORTCUTS.TOMORROW') }}
        </button>
        <button
          type="button"
          class="flex-1 text-sm py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors whitespace-nowrap"
          :class="
            selectedDate === null
              ? 'text-n-brand border-n-brand'
              : 'text-n-slate-12'
          "
          @click="clearDate"
        >
          {{ t('TASKS.DATE_SHORTCUTS.NO_DATE') }}
        </button>
      </div>

      <!-- Cabeçalho do mês -->
      <div class="flex items-center justify-between px-4 py-2">
        <div class="flex items-center">
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevYear"
          >
            <span class="i-lucide-chevrons-left size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevMonth"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
        </div>
        <span class="text-sm font-medium text-n-slate-12">
          {{ monthLabel }} {{ viewYear }}
        </span>
        <div class="flex items-center">
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextMonth"
          >
            <span class="i-lucide-chevron-right size-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextYear"
          >
            <span class="i-lucide-chevrons-right size-4" />
          </button>
        </div>
      </div>

      <!-- Grade de dias -->
      <div class="px-3 pb-2">
        <div class="grid grid-cols-7 mb-1">
          <span
            v-for="wd in WEEKDAYS"
            :key="wd"
            class="text-center text-[11px] font-medium text-n-slate-9 py-1"
          >
            {{ wd }}
          </span>
        </div>
        <div class="grid grid-cols-7">
          <button
            v-for="(day, i) in calendarDays"
            :key="i"
            type="button"
            class="flex items-center justify-center h-8 text-sm rounded transition-colors"
            :class="[
              isSelected(day.date)
                ? 'bg-n-brand text-white font-medium'
                : isToday(day.date)
                  ? 'border border-n-brand text-n-brand font-medium hover:bg-n-alpha-2'
                  : day.currentMonth
                    ? 'text-n-slate-12 hover:bg-n-alpha-1'
                    : 'text-n-slate-9 hover:bg-n-alpha-1',
            ]"
            @click="selectDay(day.date)"
          >
            {{ day.date.getDate() }}
          </button>
        </div>
      </div>

      <!-- Campo de horário -->
      <div
        class="border-t border-n-weak flex items-center pl-[24px] pr-3 py-1.5"
        @click.stop
      >
        <!-- Ícone fixo sempre presente -->
        <span
          class="i-lucide-clock size-3.5 flex-shrink-0 mr-2"
          :class="timeRaw.length >= 1 ? 'text-n-brand' : 'text-n-slate-9'"
        />
        <!-- Botão "Adicionar horário" ou input — ocupa o restante -->
        <button
          v-if="!showTimeInput && timeRaw.length === 0"
          type="button"
          class="flex-1 text-left text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @mousedown.prevent.stop="
            showTimeInput = true;
            nextTick(() => timeInputEl?.focus());
          "
        >
          {{ t('TASKS.DATE_SHORTCUTS.ADD_TIME') }}
        </button>
        <div
          v-else
          class="inline-flex items-center gap-2 px-3 py-1 cursor-text bg-n-surface-2 rounded-lg"
          @click="timeInputEl && timeInputEl.focus()"
        >
          <input
            ref="timeInputEl"
            type="text"
            inputmode="numeric"
            maxlength="5"
            size="5"
            :value="formatTimeDisplay(timeRaw)"
            class="reset-base bg-n-surface-2 text-sm font-medium text-n-slate-12 focus:outline-none bg-none border-0 p-0 m-0 shadow-none w-12 h-5 leading-5 block"
            @focus="
              showTimeInput = true;
              syncInputDisplay();
            "
            @keydown="onTimeKeydown"
            @blur="showTimeInput = false"
          />
          <span
            class="i-lucide-x size-3 text-n-ruby-9 hover:text-n-ruby-11 cursor-pointer transition-colors flex-shrink-0"
            @mousedown.stop.prevent="clearTime()"
          />
        </div>
      </div>

      <!-- Recorrência -->
      <div v-if="!hideRecurrence" class="border-t border-n-weak">
        <div class="relative">
          <button
            type="button"
            class="w-full flex items-center gap-2 pl-[24px] pr-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="recurrenceType ? 'text-n-brand' : 'text-n-slate-11'"
            @click="showRecurrenceMenu = !showRecurrenceMenu"
          >
            <span class="i-lucide-repeat size-3.5 flex-shrink-0" />
            <span class="flex-1 text-left">
              {{
                recurrenceType
                  ? t(`TASKS.RECURRENCE.${recurrenceType.toUpperCase()}`)
                  : t('TASKS.RECURRENCE.LABEL')
              }}
            </span>
            <span
              v-if="recurrenceType"
              class="i-lucide-x size-3.5 text-n-slate-9 hover:text-n-ruby-11"
              @click.stop="clearRecurrence"
            />
            <span
              v-else
              class="i-lucide-chevron-down size-3.5 text-n-slate-9"
              :class="{ 'rotate-180': showRecurrenceMenu }"
            />
          </button>

          <div
            v-if="showRecurrenceMenu"
            class="absolute bottom-full left-0 w-full bg-n-background border border-n-weak rounded-xl shadow-xl py-1 z-30"
            @click.stop
          >
            <button
              v-for="opt in RECURRENCE_OPTIONS"
              :key="opt.key"
              type="button"
              class="w-full flex items-center gap-2 pl-[24px] pr-3 py-2.5 text-sm hover:bg-n-alpha-1 transition-colors"
              :class="
                recurrenceType === opt.key
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
              "
              @click="selectRecurrence(opt.key)"
            >
              <span
                v-if="recurrenceType === opt.key"
                class="i-lucide-check size-3.5 flex-shrink-0"
              />
              <span v-else class="size-3.5 flex-shrink-0" />
              {{ opt.label() }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de recorrência personalizada -->
    <div
      v-if="showCustomModal"
      class="absolute top-0 left-0 w-full z-50 bg-n-background border border-n-weak rounded-xl shadow-xl overflow-hidden"
      @click.stop
    >
      <!-- Cabeçalho -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('TASKS.RECURRENCE.CUSTOM_TITLE') }}
        </span>
        <button
          type="button"
          class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-9 hover:text-n-slate-12 transition-colors"
          @click="cancelCustom"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <!-- Repetir a cada -->
      <div class="px-4 py-3 border-b border-n-weak">
        <p class="text-xs text-n-slate-10 mb-2">
          {{ t('TASKS.RECURRENCE.REPEAT_EVERY') }}
        </p>
        <div class="flex items-center gap-2">
          <!-- Número -->
          <div
            class="flex items-center border border-n-weak rounded-lg overflow-hidden bg-n-surface-2 w-24"
          >
            <button
              type="button"
              class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none"
              @click="customInterval = Math.max(1, customInterval - 1)"
            >
              −
            </button>
            <span
              class="flex-1 text-center text-sm font-medium text-n-slate-12 tabular-nums"
            >
              {{ customInterval }}
            </span>
            <button
              type="button"
              class="px-2.5 py-2 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none"
              @click="customInterval++"
            >
              +
            </button>
          </div>
          <!-- Unidade — botões estilo chip -->
          <div class="flex gap-1 flex-wrap">
            <button
              v-for="u in UNIT_OPTIONS"
              :key="u.key"
              type="button"
              class="px-2.5 py-1.5 text-sm rounded-lg border transition-colors"
              :class="
                customUnit === u.key
                  ? 'border-n-brand text-n-brand bg-n-alpha-2'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="customUnit = u.key"
            >
              {{ u.label() }}
            </button>
          </div>
        </div>
      </div>

      <!-- Termina em -->
      <div class="px-4 py-3">
        <p class="text-xs text-n-slate-10 mb-2">
          {{ t('TASKS.RECURRENCE.ENDS') }}
        </p>
        <div class="flex flex-col gap-1">
          <!-- Nunca -->
          <button
            type="button"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="
              endsMode === 'never'
                ? 'bg-n-alpha-2 text-n-brand'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="endsMode = 'never'"
          >
            <span
              class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
              :class="endsMode === 'never' ? 'border-n-brand' : 'border-n-weak'"
            >
              <span
                v-if="endsMode === 'never'"
                class="size-2 rounded-full bg-n-brand"
              />
            </span>
            {{ t('TASKS.RECURRENCE.NEVER') }}
          </button>

          <!-- Em data -->
          <button
            type="button"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="
              endsMode === 'on_date'
                ? 'bg-n-alpha-2 text-n-brand'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="endsMode = 'on_date'"
          >
            <span
              class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
              :class="
                endsMode === 'on_date' ? 'border-n-brand' : 'border-n-weak'
              "
            >
              <span
                v-if="endsMode === 'on_date'"
                class="size-2 rounded-full bg-n-brand"
              />
            </span>
            <span class="flex-shrink-0">{{
              t('TASKS.RECURRENCE.ON_DATE')
            }}</span>
            <input
              v-model="endsAt"
              type="date"
              :disabled="endsMode !== 'on_date'"
              class="ml-auto text-sm bg-n-surface-2 border border-n-weak rounded-lg px-2 py-1 outline-none focus:border-n-brand disabled:opacity-40 text-n-slate-12"
              @click.stop
            />
          </button>

          <!-- Após N ocorrências -->
          <button
            type="button"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
            :class="
              endsMode === 'after'
                ? 'bg-n-alpha-2 text-n-brand'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="endsMode = 'after'"
          >
            <span
              class="size-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
              :class="endsMode === 'after' ? 'border-n-brand' : 'border-n-weak'"
            >
              <span
                v-if="endsMode === 'after'"
                class="size-2 rounded-full bg-n-brand"
              />
            </span>
            <span class="flex-shrink-0">{{ t('TASKS.RECURRENCE.AFTER') }}</span>
            <div
              class="flex items-center border border-n-weak rounded-lg overflow-hidden bg-n-surface-2 ml-auto"
              @click.stop
            >
              <button
                type="button"
                :disabled="endsMode !== 'after'"
                class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none disabled:opacity-40"
                @click.stop="endsCount = Math.max(1, endsCount - 1)"
              >
                −
              </button>
              <span
                class="w-8 text-center text-sm font-medium text-n-slate-12 tabular-nums"
              >
                {{ endsCount }}
              </span>
              <button
                type="button"
                :disabled="endsMode !== 'after'"
                class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-sm leading-none disabled:opacity-40"
                @click.stop="endsCount++"
              >
                +
              </button>
            </div>
            <span class="text-n-slate-10 text-xs flex-shrink-0">{{
              t('TASKS.RECURRENCE.OCCURRENCES')
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
